import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supplierService } from '../../services/supplierService';
import { Supplier, CreateSupplierRequest } from '../../types';

// Define the initial state interface
interface SuppliersState {
  suppliers: Supplier[];
  selectedSupplier: Supplier | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

// Define the initial state
const initialState: SuppliersState = {
  suppliers: [],
  selectedSupplier: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

// Async thunks
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const suppliers = await supplierService.getSuppliers();
      return suppliers;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch suppliers');
    }
  }
);

export const fetchSupplierById = createAsyncThunk(
  'suppliers/fetchSupplierById',
  async (supplierId: string, { rejectWithValue }) => {
    try {
      const supplier = await supplierService.getSupplierById(supplierId);
      return supplier;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch supplier');
    }
  }
);

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplierData: CreateSupplierRequest, { rejectWithValue, dispatch }) => {
    try {
      const newSupplier = await supplierService.createSupplier(supplierData);
      // Refresh the suppliers list
      dispatch(fetchSuppliers());
      return newSupplier;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create supplier');
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ id, data }: { id: string; data: Partial<Supplier> }, { rejectWithValue, dispatch }) => {
    try {
      const updatedSupplier = await supplierService.updateSupplier(id, data);
      // Refresh the suppliers list
      dispatch(fetchSuppliers());
      return updatedSupplier;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update supplier');
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (supplierId: string, { rejectWithValue, dispatch }) => {
    try {
      await supplierService.deleteSupplier(supplierId);
      // Refresh the suppliers list
      dispatch(fetchSuppliers());
      return supplierId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete supplier');
    }
  }
);

// Create the suppliers slice
const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSupplier: (state, action: PayloadAction<Supplier | null>) => {
      state.selectedSupplier = action.payload;
    },
    clearSelectedSupplier: (state) => {
      state.selectedSupplier = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suppliers = action.payload;
        state.error = null;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch supplier by ID
      .addCase(fetchSupplierById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedSupplier = action.payload;
        state.error = null;
      })
      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create supplier
      .addCase(createSupplier.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createSupplier.fulfilled, (state) => {
        state.isCreating = false;
        state.error = null;
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update supplier
      .addCase(updateSupplier.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateSupplier.fulfilled, (state) => {
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete supplier
      .addCase(deleteSupplier.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteSupplier.fulfilled, (state) => {
        state.isDeleting = false;
        state.error = null;
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearError, setSelectedSupplier, clearSelectedSupplier } = suppliersSlice.actions;

// Export selectors
export const selectSuppliers = (state: any) => state.suppliers.suppliers;
export const selectSelectedSupplier = (state: any) => state.suppliers.selectedSupplier;
export const selectSuppliersLoading = (state: any) => state.suppliers.isLoading;
export const selectSuppliersError = (state: any) => state.suppliers.error;
export const selectSuppliersCreating = (state: any) => state.suppliers.isCreating;
export const selectSuppliersUpdating = (state: any) => state.suppliers.isUpdating;
export const selectSuppliersDeleting = (state: any) => state.suppliers.isDeleting;

// Export reducer
export default suppliersSlice.reducer;
